/**
 * Biblioteca de prompts.
 *
 * Duas utilidades no mesmo lugar:
 *  1. material de estudo — o que é IA, o que é prompt, anatomia, métodos e erros
 *     comuns —, usado na área do cliente e nas páginas impressas do caderno;
 *  2. prompts de exemplo prontos para copiar e adaptar.
 *
 * Todo o texto é original, escrito para este gerador: pode ser impresso e
 * vendido com o miolo.
 */
export const CATEGORIAS_PROMPT = [
    'ESCRITA',
    'ESTUDOS',
    'TRABALHO',
    'NEGOCIOS',
    'CRIATIVO',
    'TECNOLOGIA',
    'PESSOAL',
    'ORGANIZACAO',
];
export const ROTULO_CATEGORIA_PROMPT = {
    ESCRITA: 'Escrita e revisão',
    ESTUDOS: 'Estudos e aprendizado',
    TRABALHO: 'Trabalho e produtividade',
    NEGOCIOS: 'Negócios e vendas',
    CRIATIVO: 'Criatividade e ideias',
    TECNOLOGIA: 'Tecnologia e dados',
    PESSOAL: 'Vida pessoal',
    ORGANIZACAO: 'Organização e planejamento',
};
export const DESCRICAO_CATEGORIA_PROMPT = {
    ESCRITA: 'Textos, e-mails, relatórios, revisão de português e tom de voz.',
    ESTUDOS: 'Explorar um assunto, criar questões, revisar e explicar com outras palavras.',
    TRABALHO: 'Atas, apresentações, prioridades e comunicação com o time.',
    NEGOCIOS: 'Propostas, precificação, atendimento ao cliente e análise de mercado.',
    CRIATIVO: 'Nomes, roteiros, ideias soltas e caminhos que você não tinha pensado.',
    TECNOLOGIA: 'Fórmulas, planilhas, consultas, revisão de código e testes.',
    PESSOAL: 'Rotina, treino, alimentação, viagens e decisões da vida prática.',
    ORGANIZACAO: 'Planejar a semana, montar checklists e transformar objetivo em tarefa.',
};
export const GUIA_PROMPTS = [
    {
        id: 'o-que-e-ia',
        titulo: 'O que é inteligência artificial',
        paragrafos: [
            'Inteligência artificial é o nome que se dá a programas capazes de fazer tarefas que antes exigiam uma pessoa: entender um texto, escrever, traduzir, resumir, comparar, classificar, prever um número a partir de dados.',
            'Os assistentes de conversa (como os que respondem em uma janela de chat) são modelos de linguagem. Eles foram treinados lendo uma quantidade enorme de textos e aprenderam a prever qual palavra faz sentido depois de outra. Por isso escrevem com naturalidade — mas não "sabem" as coisas como uma pessoa sabe.',
            'Três consequências práticas: o resultado é sempre uma probabilidade, não uma certeza; a mesma pergunta pode gerar respostas diferentes; e a ferramenta não tem como saber que errou. Confiar sem revisar é o erro mais caro que se comete com IA.',
        ],
        itens: [
            'Bom para: rascunhar, organizar, resumir, traduzir, comparar opções, gerar variações.',
            'Ruim para: decidir por você, dar números oficiais, responder sobre algo sigiloso, substituir um especialista.',
            'Regra de ouro: a IA propõe, você decide.',
        ],
    },
    {
        id: 'o-que-e-prompt',
        titulo: 'O que é um prompt',
        paragrafos: [
            'Prompt é o pedido que você faz para a IA. Pode ser uma pergunta curta ("me dá ideias de nome para uma agenda") ou um enunciado completo, com papel, contexto, tarefa, formato e limites.',
            'A diferença entre um pedido curto e um prompt bem escrito é a quantidade de decisões que você já tomou antes de pedir. Quanto mais claro o pedido, menos a IA precisa adivinhar — e menos você precisa reescrever depois.',
        ],
        itens: [
            'Pergunta: "faz um texto sobre produtividade".',
            'Prompt: "Escreva um e-mail de 120 palavras para a equipe de vendas, em tom direto e cordial, lembrando que o fechamento do mês é na sexta e que os relatórios precisam ser enviados até quinta às 18h."',
        ],
    },
    {
        id: 'anatomia',
        titulo: 'Anatomia de um bom prompt',
        paragrafos: [
            'Um prompt forte costuma ter sete peças. Não é obrigatório usar todas, mas quando algo sair errado, geralmente a peça que faltou está nesta lista.',
        ],
        itens: [
            'Papel: quem a IA deve ser naquela conversa ("você é uma revisora de textos jornalísticos").',
            'Tarefa: o que exatamente precisa ser feito, com um verbo no imperativo ("reescreva", "compare", "liste").',
            'Contexto: o que a IA precisa saber e não vive na cabeça dela ("o produto é uma agenda impressa em A5, vendida para noivas").',
            'Formato da resposta: lista, tabela, parágrafo único, 5 títulos, JSON, e-mail pronto.',
            'Restrições: tamanho, tom, o que evitar, idioma, prazo, público.',
            'Exemplos: um ou dois modelos do resultado ideal (ou do que você não quer).',
            'Critério de sucesso: como você vai julgar se ficou bom ("serve se der para copiar e colar sem editar").',
        ],
    },
    {
        id: 'metodos',
        titulo: 'Métodos para montar prompts',
        paragrafos: [
            'Existem vários "métodos" com nomes diferentes; no fundo, todos organizam as mesmas peças. Escolha o que combina com a tarefa.',
        ],
        itens: [
            'Persona: diga o papel e o público. "Aja como nutricionista esportivo explicando para um iniciante de 45 anos."',
            'Tarefa + formato: diga o verbo e o formato. "Liste 10 ideias e coloque cada uma em uma linha, começando por um verbo."',
            'Passo a passo: peça o raciocínio em etapas antes da resposta final. Útil em contas, planos e decisões com trade-offs.',
            'Poucos exemplos: mostre 2 ou 3 pares "entrada → saída" e peça para seguir o padrão. É o método mais eficaz para manter estilo e estrutura.',
            'Rascunho + crítica: peça uma primeira versão, depois peça que a própria IA aponte 5 problemas e reescreva. Você recebe um texto bem melhor com metade do esforço.',
            'Entrevista: peça que a IA faça perguntas antes de responder. Excelente quando você não sabe explicar o problema ainda.',
            'Restrições duras: seja explícito no que não pode ("sem clichê", "sem citar marcas", "no máximo 140 caracteres", "não invente números").',
            'Iteração guiada: ajuste uma coisa por vez — tom, tamanho, exemplos — e guarde a versão que funcionou. É assim que nasce o seu prompt de estimação.',
        ],
    },
    {
        id: 'erros',
        titulo: 'Erros comuns e como evitar',
        paragrafos: [
            'Quase todo prompt ruim repete um destes erros. A boa notícia é que a correção é sempre a mesma: dizer explicitamente o que você quer.',
        ],
        itens: [
            'Pedir tudo de uma vez: divida em etapas e aprove cada uma.',
            'Não dizer o público: "explique como se eu fosse leigo" muda completamente o resultado.',
            'Não definir o formato: sem isso, a resposta sai em blocos difíceis de reaproveitar.',
            'Aceitar a primeira resposta: a segunda versão, pedida com ajuste claro, costuma ser muito melhor.',
            'Não revisar: números, nomes, leis, datas e referências precisam de conferência humana.',
            'Enviar dado sensível: documentos de clientes, senhas e informações pessoais não devem entrar no chat.',
        ],
    },
    {
        id: 'como-registrar',
        titulo: 'Como testar e registrar seus prompts',
        paragrafos: [
            'Prompt bom é prompt testado. O registro vale mais do que a memória: anote o modelo, a data, o resultado e o ajuste que fez diferença. Em poucas semanas você tem um acervo pessoal que economiza horas.',
        ],
        itens: [
            'Guarde o prompt exatamente como funcionou, incluindo a parte "não faça tal coisa".',
            'Anote o modelo e a versão: a mesma frase se comporta diferente entre ferramentas e entre versões.',
            'Registre o resultado em uma linha: serviu, serviu em parte, não serviu.',
            'Marque como favorito o que você usa toda semana.',
            'Uma ficha por prompt, com categoria — assim você acha rápido quando o problema reaparece.',
        ],
    },
];
export const PROMPTS_EXEMPLO = [
    {
        id: 'revisar-texto',
        titulo: 'Revisar um texto sem mudar meu jeito de escrever',
        categoria: 'ESCRITA',
        objetivo: 'Corrigir clareza e gramática preservando o estilo original.',
        conteudo: 'Aja como revisor de textos em português do Brasil. Vou colar um texto meu.\n' +
            'Tarefa: revise sem mudar meu estilo de escrita.\n' +
            'Como responder:\n' +
            '1. lista com os problemas encontrados (clareza, repetição, gramática, pontuação);\n' +
            '2. o texto revisado;\n' +
            '3. as três mudanças que mais melhoraram o resultado.\n' +
            'Restrições: não troque palavras de estilo próprio, não aumente o tamanho, mantenha o tom.\n' +
            'Texto:\n"""\n(cole aqui)\n"""',
        dica: 'Se o texto for longo, peça a revisão por blocos e diga o que mais te incomoda antes de revisar.',
    },
    {
        id: 'email-dificil',
        titulo: 'Escrever um e-mail difícil',
        categoria: 'ESCRITA',
        objetivo: 'Dizer algo incômodo com clareza e sem criar conflito.',
        conteudo: 'Escreva um e-mail profissional em português do Brasil.\n' +
            'Contexto: (descreva a situação em 3 linhas)\n' +
            'Destinatário: (quem é, qual a relação)\n' +
            'Objetivo do e-mail: (o que precisa acontecer depois da leitura)\n' +
            'Restrições: máximo 150 palavras; tom cordial e direto; sem ironia; sem "espero que esteja bem"; inclua um próximo passo com prazo.\n' +
            'Entregue o assunto do e-mail e o corpo. Depois, ofereça uma versão mais firme e uma mais leve.',
        dica: 'Diga o que já tentou antes; a IA evita repetir o caminho que não funcionou.',
    },
    {
        id: 'explicar-simples',
        titulo: 'Explicar um assunto difícil',
        categoria: 'ESTUDOS',
        objetivo: 'Entender algo complexo em linguagem simples, com analogia.',
        conteudo: 'Explique o conceito de (assunto) para alguém sem conhecimento técnico.\n' +
            'Formato:\n' +
            '1. explicação em até 5 frases;\n' +
            '2. uma analogia do dia a dia;\n' +
            '3. um exemplo prático;\n' +
            '4. os três erros de interpretação mais comuns;\n' +
            '5. três perguntas para eu testar se entendi (com respostas no fim).\n' +
            'Não use jargão sem explicar. Se algo for incerto, diga que é incerto.',
        dica: 'Peça a mesma explicação em três níveis: criança, curioso e especialista.',
    },
    {
        id: 'plano-estudos',
        titulo: 'Plano de estudo com prazo curto',
        categoria: 'ESTUDOS',
        objetivo: 'Transformar um assunto grande em rotina possível.',
        conteudo: 'Monte um plano de estudo para (assunto), em (número) semanas, com (horas) por dia.\n' +
            'Regras: sessões de no máximo 50 minutos; separar 20% do tempo para revisão; incluir uma autoavaliação por semana; dizer o que fazer se eu atrasar dois dias.\n' +
            'Formato: tabela com semana, tema, objetivo da semana, tarefa diária e forma de medir o progresso.',
        dica: 'Informe seu ponto de partida real: o que já sabe e onde trava.',
    },
    {
        id: 'ata-reuniao',
        titulo: 'Transformar anotações em ata',
        categoria: 'TRABALHO',
        objetivo: 'Sair da reunião com ata objetiva e tarefas claras.',
        conteudo: 'Transforme minhas anotações bagunçadas em uma ata de reunião.\n' +
            'Estrutura: data e participantes; pauta; decisões tomadas; tarefas (tarefa, responsável, prazo); pontos abertos; próxima reunião.\n' +
            'Regras: não invente informação; o que estiver ambíguo entra em "pontos abertos" com a pergunta a esclarecer.\n' +
            'Anotações:\n"""\n(cole aqui)\n"""',
        dica: 'Se tiver transcrição, peça a ata em duas versões: uma de meia página e outra completa.',
    },
    {
        id: 'priorizar-semana',
        titulo: 'Priorizar a semana quando tudo é urgente',
        categoria: 'ORGANIZACAO',
        objetivo: 'Separar o que realmente importa do que só faz barulho.',
        conteudo: 'Aja como um gestor experiente em priorização.\n' +
            'Lista de pendências: (cole a lista)\n' +
            'Contexto: (prazos reais, o que depende de outras pessoas, o que é inegociável)\n' +
            'Tarefa: organize em quatro blocos — fazer hoje, fazer nesta semana, delegar ou negociar prazo, descartar.\n' +
            'Para cada item: por que ficou nesse bloco e qual o próximo passo concreto (um só).\n' +
            'No fim, aponte o que eu deveria dizer "não" nesta semana.',
        dica: 'Peça também o que fazer se uma urgência real aparecer no meio do dia.',
    },
    {
        id: 'proposta-comercial',
        titulo: 'Estruturar uma proposta comercial',
        categoria: 'NEGOCIOS',
        objetivo: 'Montar proposta clara, com valor percebido, sem parecer catálogo.',
        conteudo: 'Estruture uma proposta comercial para (produto ou serviço) vendida a (tipo de cliente).\n' +
            'Contexto: o cliente disse que (o que ele falou), o problema dele é (descreva), o orçamento previsto é (faixa).\n' +
            'Formato: 1) o problema em uma frase; 2) o que está incluído e o que não está; 3) como funciona o processo, em etapas; 4) prazos; 5) investimento e formas de pagamento; 6) próximos passos; 7) três perguntas frequentes.\n' +
            'Regras: sem promessa de resultado que não posso garantir; linguagem simples; no máximo 1 página.',
        dica: 'Peça uma versão curta de 10 linhas para mandar no WhatsApp antes da proposta completa.',
    },
    {
        id: 'preco-servico',
        titulo: 'Revisar meu preço',
        categoria: 'NEGOCIOS',
        objetivo: 'Encontrar o preço certo e saber justificá-lo.',
        conteudo: 'Aja como consultor de precificação para pequenos negócios.\n' +
            'Dados: custo direto por unidade/atendimento (valor), tempo gasto (horas), custos fixos mensais (valor), quantos clientes por mês (número), preço atual (valor), preço dos concorrentes (valor).\n' +
            'Tarefa: calcule três cenários (conservador, equilibrado, agressivo), aponte o que muda em cada um, e escreva a justificativa de valor em uma frase que eu possa dizer ao cliente sem parecer defensivo.',
        dica: 'Se faltar dado, a IA deve listar o que precisa antes de calcular.',
    },
    {
        id: 'ideias-conteudo',
        titulo: 'Ideias de conteúdo para a semana',
        categoria: 'CRIATIVO',
        objetivo: 'Sair do vazio criativo com ideias aplicáveis.',
        conteudo: 'Gere 15 ideias de conteúdo para (rede social ou canal) sobre (tema), falando com (público).\n' +
            'Para cada ideia: título em até 8 palavras, o gancho da primeira linha, o formato (carrossel, vídeo curto, texto) e o que a pessoa leva de útil.\n' +
            'Regras: nada genérico do tipo "dicas de produtividade"; pelo menos 5 ideias devem partir de erros comuns do público; sem clichê de começo de texto.',
        dica: 'Peça que a IA critique as 15 e escolha as 5 mais fortes, justificando.',
    },
    {
        id: 'nome-de-projeto',
        titulo: 'Nomes para um projeto ou produto',
        categoria: 'CRIATIVO',
        objetivo: 'Chegar em opções de nome que soem bem em português.',
        conteudo: 'Sugira 30 nomes para (o que é), voltado a (público), com a ideia de (sentimento que quer passar).\n' +
            'Organize em três grupos: diretos, metafóricos e inventados.\n' +
            'Para os 6 melhores: como soa falado no telefone, o que pode confundir, e uma frase de apresentação em uma linha.\n' +
            'Restrições: fácil de escrever depois de ouvir; sem acento difícil; sem nome parecido com marca conhecida.',
        dica: 'Rode de novo pedindo apenas nomes de duas sílabas; a variação costuma revelar o melhor.',
    },
    {
        id: 'comparar-opcoes',
        titulo: 'Comparar duas alternativas',
        categoria: 'ORGANIZACAO',
        objetivo: 'Decidir com critérios explícitos, sem ficar em círculo.',
        conteudo: 'Quero decidir entre (opção A) e (opção B).\n' +
            'Contexto: (o que importa para mim, prazo, dinheiro, tempo disponível, riscos)\n' +
            'Tarefa: monte uma tabela comparando as duas em critérios objetivos (escolha os critérios e explique por quê). Depois escreva o melhor cenário e o pior cenário de cada uma.\n' +
            'Termine com: o que você faria no meu lugar e qual seria o sinal de que a escolha precisa ser revista.',
        dica: 'Se você já tem um palpite, diga: a IA tende a questionar melhor do que só concordar.',
    },
    {
        id: 'plano-alimentar',
        titulo: 'Organizar a alimentação da semana',
        categoria: 'PESSOAL',
        objetivo: 'Planejar refeições realistas com o que já existe em casa.',
        conteudo: 'Monte um plano de refeições para 7 dias para (número) pessoas.\n' +
            'Restrições: (restrições alimentares), tempo máximo de preparo (minutos) por refeição, ingredientes que já tenho: (lista), orçamento semanal (valor).\n' +
            'Formato: tabela com dia, almoço, jantar e o que preparar de uma vez só (aproveitamento).\n' +
            'No fim: lista de compras organizada por setor do mercado.\n' +
            'Importante: não substitui orientação de nutricionista; sinalize se alguma restrição exigir acompanhamento.',
        dica: 'Peça uma lista de substituições para quando faltar ingrediente.',
    },
    {
        id: 'treino-iniciante',
        titulo: 'Treino para começar de novo',
        categoria: 'PESSOAL',
        objetivo: 'Voltar a treinar com um plano seguro e progressivo.',
        conteudo: 'Monte um treino de 4 semanas, 3 vezes por semana, para alguém voltando a treinar depois de muito tempo parado.\n' +
            'Contexto: (idade aproximada), (equipamentos disponíveis), tempo por sessão (minutos), histórico de dor ou lesão (descreva).\n' +
            'Formato: tabela por semana com exercícios, séries, repetições e descanso; como aumentar a carga; o que fazer se sentir dor.\n' +
            'Regras: progressão leve; incluir mobilidade; avisar quando procurar um profissional de educação física ou médico.',
        dica: 'Peça a versão de 20 minutos para os dias em que o tempo apertar.',
    },
    {
        id: 'explicar-codigo',
        titulo: 'Explicar e melhorar um trecho de código',
        categoria: 'TECNOLOGIA',
        objetivo: 'Entender o que o código faz e o que pode dar errado.',
        conteudo: 'Aja como desenvolvedor sênior revisando código.\n' +
            'Linguagem: (linguagem e versão).\n' +
            'Código:\n```\n(cole aqui)\n```\n' +
            'Tarefa: 1) explique o que o código faz, linha por linha, em linguagem simples; 2) aponte erros e casos de borda; 3) sugira uma versão melhorada, explicando cada mudança; 4) liste três testes que eu deveria escrever.\n' +
            'Regras: não reescreva o que não precisa; se algo depender de contexto que você não tem, pergunte antes.',
        dica: 'Cole também a mensagem de erro completa; o diagnóstico fica muito mais preciso.',
    },
    {
        id: 'formula-planilha',
        titulo: 'Fórmula de planilha explicada',
        categoria: 'TECNOLOGIA',
        objetivo: 'Resolver a fórmula e entender como ela funciona.',
        conteudo: 'Preciso de uma fórmula de (Excel | Google Sheets) que (descreva o resultado esperado).\n' +
            'Estrutura da planilha: (quais colunas existem, com nomes e exemplos de dados).\n' +
            'Entregue: a fórmula pronta; uma explicação de cada parte; uma alternativa mais simples, se existir; e dois casos em que a fórmula falharia.\n' +
            'Se houver mais de uma forma de resolver, compare as opções em uma tabela curta.',
        dica: 'Digite dois exemplos de linhas reais: a fórmula sai ajustada à sua planilha.',
    },
    {
        id: 'melhorar-rotina',
        titulo: 'Consertar uma rotina que não acontece',
        categoria: 'ORGANIZACAO',
        objetivo: 'Descobrir por que o plano falha e ajustar na prática.',
        conteudo: 'Aja como quem ajuda pessoas a criar hábitos sustentáveis.\n' +
            'Rotina que eu tento fazer: (descreva). O que costuma acontecer: (descreva). Horários disponíveis: (descreva).\n' +
            'Tarefa: 1) liste as três causas mais prováveis da falha; 2) proponha uma versão menor e possível para começar amanhã; 3) diga como medir em uma semana; 4) sugira o que fazer quando eu perder dois dias seguidos.\n' +
            'Sem discurso motivacional: quero um plano concreto.',
        dica: 'Seja específico sobre o que acontece no dia em que dá errado.',
    },
    {
        id: 'critica-de-ideia',
        titulo: 'Criticar uma ideia antes de investir tempo',
        categoria: 'CRIATIVO',
        objetivo: 'Achar os furos antes de gastar dinheiro e meses.',
        conteudo: 'Vou lançar (ideia). Contexto: (público, problema que resolve, como penso em cobrar, recursos que tenho).\n' +
            'Tarefa: aja como um investidor cético. 1) os três pontos mais frágeis; 2) qual suposição, se estiver errada, derruba tudo; 3) o menor teste possível para validar em duas semanas; 4) sinais de que devo desistir.\n' +
            'Regras: sem elogio de cortesia; seja direto e específico.',
        dica: 'Peça também a visão otimista, para comparar os dois lados.',
    },
    {
        id: 'resumo-de-material',
        titulo: 'Resumir um material longo',
        categoria: 'ESTUDOS',
        objetivo: 'Extrair o essencial e o que preciso fazer com a informação.',
        conteudo: 'Resuma o material abaixo para (objetivo: estudar para prova | decidir | aplicar no trabalho).\n' +
            'Formato: 1) cinco frases com as ideias centrais; 2) conceitos-chave em lista, cada um com uma linha; 3) dados e números citados; 4) o que é opinião do autor e o que é fato; 5) três perguntas que o material responde e duas que ele deixa em aberto.\n' +
            'Não invente nada que não esteja no texto. Se algo estiver ambíguo, marque como ambíguo.\n' +
            'Material:\n"""\n(cole aqui)\n"""',
        dica: 'Para textos muito longos, peça primeiro o índice de assuntos e depois resuma por partes.',
    },
];
/** Rótulos usados nas fichas impressas e no cadastro da área do cliente. */
export const CAMPOS_PROMPT = [
    'Título',
    'Categoria',
    'Objetivo',
    'Prompt',
    'Modelo e versão',
    'Resultado',
    'O que ajustei',
];
export function exemplosPorCategoria() {
    return CATEGORIAS_PROMPT.map((categoria) => ({
        categoria,
        exemplos: PROMPTS_EXEMPLO.filter((exemplo) => exemplo.categoria === categoria),
    })).filter((entrada) => entrada.exemplos.length > 0);
}
//# sourceMappingURL=prompts.js.map